import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IMAGE_URL } from "network";
import { useEffect, useState } from "react";
import { categoryOptions } from "./data";

// const steps = ["Select Category For", "Category Details", "SEO Data"];
const steps = ["Category Details", "SEO Data"];

function AddForm({ open, onClose, initialData, onSubmit }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewUrl2, setPreviewUrl2] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [step, setStep] = useState(0);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      _id: initialData?._id || "",
      name: initialData?.name || "",
      image: null,
      metaTitle: initialData?.metaTitle || "",
      metaDescription: initialData?.metaDescription || "",
      keywords: initialData?.keywords || [],
      backgroundImage: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Category name is required"),
      metaTitle: Yup.string().required("Meta title is required"),
      metaDescription: Yup.string().required("Meta description is required"),
      image: Yup.mixed()
        .nullable()
        .test("fileRequired", "Image is required", function (value) {
          return initialData?.image || value;
        }),
      backgroundImage: Yup.mixed()
        .nullable()
        .test(
          "fileRequired",
          "Background Image  is required",
          function (value) {
            return initialData?.backgroundImage || value;
          }
        ),
    }),
    onSubmit: async (values) => {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length) return; // prevent submit if there are errors
      await onSubmit(values);
      handleCancel();
    },
  });

  useEffect(() => {
    if (initialData?.image) {
      setPreviewUrl(IMAGE_URL + initialData.image);
    }
    if (initialData?.backgroundImage) {
      setPreviewUrl2(IMAGE_URL + initialData.backgroundImage);
    }
  }, [initialData]);

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !formik.values.keywords.includes(trimmed)) {
      formik.setFieldValue("keywords", [...formik.values.keywords, trimmed]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (kw) => {
    formik.setFieldValue(
      "keywords",
      formik.values.keywords.filter((k) => k !== kw)
    );
  };

  const handleFileChange = (field, file) => {
    const cleanField = field.trim(); // optional safety
    formik.setFieldValue(cleanField, file);
    if (file) {
      const url = URL.createObjectURL(file);
      if (cleanField === "image") {
        setPreviewUrl(url);
      } else if (cleanField === "backgroundImage") {
        setPreviewUrl2(url);
      }
    }
  };

  const handleNext = async () => {
    const fieldsToValidatePerStep = {
      // 0: ["module_type"],
      0: ["name", "image"],
      1: ["metaTitle", "metaDescription"],
    };

    const fields = fieldsToValidatePerStep[step];

    await formik.validateForm();
    const hasErrors = fields.some((field) => !!formik.errors[field]);

    if (hasErrors) {
      formik.setTouched(
        fields.reduce((acc, field) => {
          acc[field] = true;
          return acc;
        }, {})
      );
      return;
    }

    setStep((prev) => prev + 1);
  };

  const handleBack = () => setStep((prev) => prev - 1);

  const handleCancel = () => {
    formik.setValues({
      _id: "",
      name: "",
      image: null,
      backgroundImage: null,
      metaTitle: "",
      metaDescription: "",
      keywords: [],
    });
    setStep(0);
    setPreviewUrl("");
    setPreviewUrl2("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Category" : "Add Category"}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Stepper activeStep={step} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {step === 0 && (
            <Box mt={3}>
              <TextField
                name="name"
                label="Category Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                fullWidth
                margin="normal"
              />

              <Typography variant="subtitle1" mt={2}>
                Background Image
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleFileChange("backgroundImage", e.target.files[0])
                }
                style={{ marginTop: 8 }}
              />
              {formik.touched.backgroundImage &&
                formik.errors.backgroundImage && (
                  <Typography color="error" variant="caption">
                    {formik.errors.backgroundImage}
                  </Typography>
                )}
              {previewUrl2 && (
                <img
                  crossOrigin="anonymous"
                  src={previewUrl2}
                  alt="Preview"
                  style={{
                    marginTop: 16,
                    width: "100px",
                    maxHeight: 200,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              )}

              <Typography variant="subtitle1" mt={2}>
                Upload Image
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange("image", e.target.files[0])}
                style={{ marginTop: 8 }}
              />
              {formik.touched.image && formik.errors.image && (
                <Typography color="error" variant="caption">
                  {formik.errors.image}
                </Typography>
              )}
              {previewUrl && (
                <img
                  crossOrigin="anonymous"
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    marginTop: 16,
                    width: "100px",
                    maxHeight: 200,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              )}
            </Box>
          )}

          {step === 1 && (
            <Box mt={3}>
              <TextField
                name="metaTitle"
                label="Meta Title"
                value={formik.values.metaTitle}
                onChange={formik.handleChange}
                error={
                  formik.touched.metaTitle && Boolean(formik.errors.metaTitle)
                }
                helperText={formik.touched.metaTitle && formik.errors.metaTitle}
                fullWidth
                margin="normal"
              />

              <TextField
                name="metaDescription"
                label="Meta Description"
                value={formik.values.metaDescription}
                onChange={formik.handleChange}
                error={
                  formik.touched.metaDescription &&
                  Boolean(formik.errors.metaDescription)
                }
                helperText={
                  formik.touched.metaDescription &&
                  formik.errors.metaDescription
                }
                fullWidth
                margin="normal"
              />

              <Box display="flex" alignItems="center" mt={2} gap={1}>
                <TextField
                  label="Add Keyword"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addKeyword();
                    }
                  }}
                  fullWidth
                />
                <Button
                  onClick={addKeyword}
                  variant="contained"
                  color="primary"
                >
                  Add
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {formik.values.keywords.map((keyword) => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    onDelete={() => removeKeyword(keyword)}
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          {step > 0 && (
            <Button onClick={handleBack} color="secondary">
              Back
            </Button>
          )}
          {step < steps.length - 1 && (
            <Button onClick={handleNext} variant="contained" color="primary">
              Next
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button type="submit" variant="contained" color="primary">
              {initialData ? "Update" : "Add"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddForm;
